import usCounties from '@/data/us-counties-2026.json';

export interface CountyOption {
  label: string;
  value: string;
}

export interface UsCountyCatalog {
  states: readonly string[];
  countiesByState: Readonly<Record<string, readonly CountyOption[]>>;
}

interface LoanLimitLocation {
  county: string;
  state: string;
}

interface LoanLimitsData {
  limits: LoanLimitLocation[];
}

/**
 * Optimal Blue uses the plain county name for ordinary counties. Preserve
 * the official display label, but strip the terminal designators live probes
 * proved OB rejects from the submitted value. Preserve every other FHFA
 * designator unless vendor evidence says otherwise.
 *
 * Mirrored server-side by `normalizeObCountyName` in
 * supabase/functions/_shared/ob-core/county.ts; parity is pinned by tests.
 */
export function toObCountyName(county: string): string {
  return county.trim().replace(
    /\s+(?:City\s+And\s+Borough|Census\s+Area|Municipality|Municipio|Borough|County|Parish)$/i,
    ''
  );
}

/** Build the small location index the pricing forms need from FHFA rows. */
export function buildUsCountyCatalog(data: LoanLimitsData): UsCountyCatalog {
  const byState = new Map<string, Map<string, CountyOption>>();

  for (const location of data.limits) {
    const state = location.state.trim().toUpperCase();
    const label = location.county.trim();
    if (!state || !label) {
      continue;
    }

    const options = byState.get(state) ?? new Map<string, CountyOption>();
    const value = toObCountyName(label);
    options.set(value, { label, value });
    byState.set(state, options);
  }

  const states = Array.from(byState.keys()).sort((a, b) => a.localeCompare(b));
  const countiesByState: Record<string, readonly CountyOption[]> = {};
  for (const state of states) {
    countiesByState[state] = Array.from(byState.get(state)?.values() ?? [])
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  return { states, countiesByState };
}

/**
 * The catalog every pricing surface reads, built once at module load from the
 * generated projection of the FHFA loan-limit artifact (see
 * scripts/reference-data/README.md). Static on purpose: there is no runtime
 * fetch, so there is no loading or failure state to design around.
 */
export const US_COUNTY_CATALOG: UsCountyCatalog = buildUsCountyCatalog(usCounties);

/**
 * Resolve a stored county string (Encompass, recon import, a user's earlier
 * entry) to the catalog option for its state. Matches the OB-form value after
 * designator stripping, case-insensitively, then falls back to an exact
 * case-insensitive display label. Never fuzzy: an unknown county stays unknown
 * so the user must pick from the list.
 */
export function findCountyOption(
  catalog: UsCountyCatalog | null | undefined,
  state: string | null | undefined,
  raw: string | null | undefined
): CountyOption | null {
  const stateCode = typeof state === 'string' ? state.trim().toUpperCase() : '';
  const candidate = typeof raw === 'string' ? raw.trim() : '';
  if (!catalog || !stateCode || !candidate) {
    return null;
  }
  const options = catalog.countiesByState[stateCode];
  if (!options) {
    return null;
  }
  const wanted = toObCountyName(candidate).toLocaleLowerCase();
  const byValue = options.find((option) => option.value.toLocaleLowerCase() === wanted);
  if (byValue) {
    return byValue;
  }
  const wantedLabel = candidate.toLocaleLowerCase();
  return options.find((option) => option.label.toLocaleLowerCase() === wantedLabel) ?? null;
}
